/**
 * FoliarRoundPage — the foliar round detail page shell (F2 Task 7).
 *
 * Replaces F1's placeholder with the real page: header (name, month, an
 * overall status pill, and the round meta line), and a blocker notice that
 * names every unmet condition rather than leaving the manager to guess.
 * Later tasks in this slice add the FilterBar, roster table and bulk-assign
 * bar into the body below.
 *
 * This page lives OUTSIDE FoliarWorkspaceLayout (it is not one of the
 * workspace's tabs), so it does not inherit the layout's resolved programme.
 * Its crumb back into the workspace must rebuild `?programme=<programmeId>`
 * — a bare `/nutrition/foliar` would silently land the manager back on the
 * tenant's ACTIVE programme rather than the one this round belongs to.
 * `programmeId` comes straight from the route, so it is available on the
 * very first render; the bare fallback exists purely for safety.
 */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../shell/PageHeader';
import {
  DataTable, FilterBar, Notice, StatusPill,
} from '../patterns';
import type { DataTableColumn, FilterBarChip, StatusVariant } from '../patterns';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { formatMonthName } from '../../dateFormat';
import {
  nutritionService, FoliarBoardRound, FoliarBoardRoundSessions, FoliarRoster, FoliarRosterRow,
} from '../../services/nutritionService';
import {
  getOperators, getMachines, SprayOperator, SprayMachine,
} from '../../services/nutritionSessionService';
import { roundState } from './foliarBoardSummary';
import type { RoundState } from './foliarBoardSummary';
import {
  filterRoster, isAwaitingSignoff, isUnassigned, orderRoster, rosterTotals, summariseRoster,
} from './foliarRosterSummary';
import type { RosterSort, RosterSortKey } from './foliarRosterSummary';
import { rangeSelect } from '../../utils/rangeSelect';
import './nutrition.css';

/** Bulk-assign result banner tone — a narrower set than the full NoticeTone
 * union (no 'info'), since every path that sets one is a warn/danger/success
 * outcome of the Assign action. */
type BulkNoticeTone = 'warn' | 'danger' | 'success';
interface BulkNotice { tone: BulkNoticeTone; text: string }

// `roundState` is IMPORTED, not redefined — foliarBoardSummary.ts is the one
// classifier both this page and FoliarBoardPage read (see that file's own
// comment on the same rule). A local copy previously disagreed with it on
// the zero-sessions case: an uncreated round reads 'notCreated' on the
// board, but a from-scratch reimplementation here fell through to 'planned'
// — reachable because FoliarBoardPage's row click opens ANY round, created
// or not (FoliarBoardPage.tsx onCreate replaces only the status cell, not
// the name link).
//
// roundState() takes a full FoliarBoardRound and reads only `.sessions`
// (nutritionService.ts:94-105 for the shape), so this adapts the roster's
// per-row list into that same counts shape rather than guessing at a
// second classification rule.
function sessionsFromRows(rows: FoliarRosterRow[]): FoliarBoardRoundSessions {
  const completedAts = rows
    .filter((r) => r.status === 'completed' || r.status === 'signed_off')
    .map((r) => r.completedAt)
    .filter((v): v is string => !!v);
  const awaitingCompletedAts = rows
    .filter(isAwaitingSignoff)
    .map((r) => r.completedAt)
    .filter((v): v is string => !!v)
    .sort();
  return {
    total: rows.length,
    draft: rows.filter((r) => r.status === 'draft').length,
    planned: rows.filter((r) => r.status === 'planned').length,
    inProgress: rows.filter((r) => r.status === 'in_progress').length,
    completed: rows.filter((r) => r.status === 'completed').length,
    incomplete: rows.filter((r) => r.status === 'incomplete').length,
    signedOff: rows.filter((r) => r.status === 'signed_off').length,
    awaitingSignoff: rows.filter(isAwaitingSignoff).length,
    oldestAwaitingCompletedAt: awaitingCompletedAts[0] ?? null,
    completedAts,
  };
}

const ROUND_STATE_VARIANT: Record<RoundState, StatusVariant> = {
  notCreated: 'queued', planned: 'info', inProgress: 'warn', complete: 'ok',
};

// Per-ROW session status (draft/planned/in_progress/completed/incomplete/
// signed_off), a distinct concept from the round-level RoundState above —
// this is not a second copy of roundState's classification, it colours the
// raw session status already on each roster row. Completed-but-unsigned
// reads as 'warn', mirroring roundState's treatment of the same case as
// still "in progress" until sign-off closes it out.
const ROW_STATUS_VARIANT: Record<string, StatusVariant> = {
  draft: 'queued',
  planned: 'info',
  in_progress: 'warn',
  completed: 'warn',
  incomplete: 'danger',
  signed_off: 'ok',
  cancelled: 'queued',
  deleted: 'queued',
};

/**
 * The ONE literal format for an operator/machine pair. `crewSummaryFor` and
 * both branches of `renderCrew` below all call this rather than each writing
 * `` `${operatorName} · ${machineName}` `` themselves — changing the
 * separator or adding a role suffix is then a single-site edit, not three
 * literals kept in sync by hand.
 */
function formatCrewMember(operatorName: string, machineName: string): string {
  return `${operatorName} · ${machineName}`;
}

/**
 * Plain-text form. Reused ONLY by the hidden per-row findability summary
 * (`:389`) — that span feeds search, so it must stay a string, never nodes.
 * Shares `isUnassigned` and `formatCrewMember` with the render form below, so
 * the two forms cannot disagree on WHO is listed or how a name is
 * formatted — only the cancelled-combo marker (a visible-UI/compliance
 * concern, not a findability one) is deliberately render-form-only.
 */
function crewSummaryFor(row: FoliarRosterRow, unassignedLabel: string): string {
  if (isUnassigned(row)) return unassignedLabel;
  return row.combos.map((c) => formatCrewMember(c.operatorName, c.machineName)).join(', ');
}

/**
 * Render form — used at the three visible call sites (crew column, expanded
 * row detail, mobile card). A cancelled combo stays VISIBLE, carrying a
 * "stood down" marker, rather than being hidden behind a disclosure: the
 * ruling behind choosing cancel over the existing hard-delete (`removeCombo`)
 * is precisely that the record of an operator having been assigned and stood
 * down survives on a document with compliance history (#476/#477 plan).
 *
 * `testIdPrefix` differs per call site so the marker's testid is unique even
 * under an UNSCOPED query — jsdom applies no CSS (Patterns.css:754-756's
 * table/card swap never takes effect there), so the table and the mobile
 * card both exist in the DOM at once regardless of viewport, and a shared
 * testid would make `getByTestId` throw on the duplicate.
 */
function renderCrew(
  row: FoliarRosterRow,
  unassignedLabel: string,
  cancelledLabel: string,
  testIdPrefix: string,
): React.ReactNode {
  if (isUnassigned(row)) return unassignedLabel;
  return row.combos.map((c, i) => (
    <React.Fragment key={c.comboId}>
      {i > 0 && ', '}
      {c.status === 'cancelled' ? (
        <span className="ntr-rst-crew__cancelled" data-testid={`${testIdPrefix}-${c.comboId}`}>
          {formatCrewMember(c.operatorName, c.machineName)} ({cancelledLabel})
        </span>
      ) : (
        <span>{formatCrewMember(c.operatorName, c.machineName)}</span>
      )}
    </React.Fragment>
  ));
}

const FoliarRoundPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { programmeId, roundId } = useParams<{ programmeId: string; roundId: string }>();

  const [roster, setRoster] = useState<FoliarRoster | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // Bulk-assign bar state. Declared here (above `load`) because `load`'s
  // success path reads `noticeRef` to decide whether to clear `bulkNotice` —
  // the guard that keeps a refresh from wiping a notice it did not set (see
  // onAssign below; this is F1 PR #449's board bug, not repeated here).
  const [operators, setOperators] = useState<SprayOperator[]>([]);
  const [machines, setMachines] = useState<SprayMachine[]>([]);
  const [operatorId, setOperatorId] = useState('');
  const [machineId, setMachineId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [bulkNotice, setBulkNotice] = useState<BulkNotice | null>(null);
  const noticeRef = useRef(false);

  const load = useCallback(async () => {
    // Both params are required by the route pattern
    // (programmes/:programmeId/rounds/:roundId), so this guards a route that
    // cannot actually be reached rather than one this page expects in
    // practice.
    if (!programmeId || !roundId) {
      setError(t('nutrition.foliar.round.notFound'));
      setLoading(false);
      return;
    }
    try {
      const data = await nutritionService.getRoundRoster(programmeId, roundId);
      setRoster(data);
      setError('');
      // Never clear a notice this refresh did not itself set — onAssign sets
      // noticeRef.current before its own refresh when it has a warn/danger
      // notice to preserve.
      if (!noticeRef.current) setBulkNotice(null);
    } catch (_err) {
      setError(t('nutrition.foliar.round.loadFailed'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programmeId, roundId]);

  useEffect(() => { load(); }, [load]);

  // Operator/machine pools — Protection's read-only pools, loaded once on
  // mount. Never written to from here.
  useEffect(() => {
    let live = true;
    Promise.all([getOperators(), getMachines()])
      .then(([ops, machs]) => {
        if (!live) return;
        setOperators(ops);
        setMachines(machs);
      })
      .catch(() => {
        // Non-fatal: the bulk bar just renders with empty selects, which
        // keeps Assign disabled (no id can be chosen) rather than blocking
        // the whole page on a pool-load failure.
      });
    return () => { live = false; };
  }, []);

  // Roster table state. Hooks must sit above every early return (see note
  // below), so these read `roster` defensively via `roster?.rows ?? []`
  // rather than waiting for the post-guard destructure further down.
  const [search, setSearch] = useState('');
  const [chip, setChip] = useState('all');
  const [sort, setSort] = useState<RosterSort>({ key: 'orchardNo', direction: 'asc' });
  const [selected, setSelected] = useState<string[]>([]);
  const [anchor, setAnchor] = useState<string | null>(null);
  // Task 10: expand-to-manage rows. A Set (never a bare string) so two rows
  // can be open at once — DataTable's expandedRowKey collapses the previous
  // row when given a string, and one of this task's tests requires both
  // B01 and B02 expanded simultaneously. Built via `Array.from(prev)` into a
  // new Set rather than `new Set(prev)` or a spread — this project targets
  // ES5 with no downlevelIteration, and both of those forms fail
  // `npm run build` while passing jest (two earlier tasks in this slice hit
  // exactly this trap).
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpanded = useCallback((orchardNo: string) => {
    setExpanded((prev) => {
      const next = new Set(Array.from(prev));
      if (next.has(orchardNo)) next.delete(orchardNo); else next.add(orchardNo);
      return next;
    });
  }, []);

  const shown = useMemo(
    () => filterRoster(roster?.rows ?? [], search, chip),
    [roster, search, chip],
  );
  const sortedShown = useMemo(() => orderRoster(shown, sort), [shown, sort]);
  // U2's documented trap (see SoilGroupingPage.tsx:170-174): orderedNames
  // must derive from the SORTED rows, never the merely-filtered `shown`.
  // rangeSelect operates on DISPLAYED order, so a shift-click after the user
  // has clicked a column header must follow what they actually see.
  const orderedNames = useMemo(() => sortedShown.map((r) => r.orchardNo), [sortedShown]);
  const tableTotals = useMemo(() => rosterTotals(sortedShown), [sortedShown]);
  const allChecked = orderedNames.length > 0 && orderedNames.every((n) => selected.includes(n));

  const onToggle = useCallback((orchardNo: string, shiftKey: boolean) => {
    setSelected((prev) => {
      if (shiftKey && anchor) return rangeSelect(prev, orderedNames, anchor, orchardNo);
      return prev.includes(orchardNo) ? prev.filter((n) => n !== orchardNo) : [...prev, orchardNo];
    });
    setAnchor(orchardNo);
  }, [anchor, orderedNames]);

  // Hooks must sit above every early return — a hook below a guard changes
  // hook order between renders and React throws. Falls back to the bare
  // /nutrition/foliar path only while programmeId is absent (safety net,
  // not the normal path — see file header).
  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('nutrition.title'), to: '/nutrition' },
    {
      label: roster?.programmeName || t('nutrition.foliar.workspace.title'),
      to: programmeId ? `/nutrition/foliar?programme=${programmeId}` : '/nutrition/foliar',
    },
    { label: roster ? roster.round.name : t('nutrition.foliar.round.crumb') },
  ]);

  if (error && !roster) {
    return (
      <div className="pat-page" data-testid="foliar-round-page">
        <Notice tone="danger" testId="round-error">{error}</Notice>
      </div>
    );
  }
  if (loading) {
    return <div className="pat-page">{t('common.loading')}</div>;
  }
  // Every path that leaves `roster` null after loading also sets `error`,
  // and the `error && !roster` branch above already catches it — so this can
  // never actually render. It exists purely as a type-narrowing guard: `roster`
  // is `FoliarRoster | null` to the compiler, and nothing short of a literal
  // `!roster` check narrows the destructure below.
  if (!roster) return null;

  const { round, rows } = roster;
  const summary = summariseRoster(rows);

  // Selection (Task 8) keys on `orchardNo`; assignBulk takes `sessionId`s —
  // map back through the full roster (not just the filtered/sorted `shown`
  // set) since a row can be selected while a filter or sort is active.
  const selectedRows = rows.filter((r) => selected.includes(r.orchardNo));
  // Dose-C fix: same unassigned-row guard as the roster table and totals row
  // (see the `tanksPlanned` column's own comment) — only rows with a real
  // machine assigned contribute to the bulk bar's tanks figure, because an
  // unassigned row has no planned tank count in existence to contribute.
  //
  // #510.4 (D2): `tankVolumeL` and `tanksPlanned` are now governed by ONE
  // agreement predicate in foliarRosterService — one machine AND one tank
  // count — so they are null together. The paired guard below is therefore
  // belt-and-braces rather than load-bearing, and it stays: the wire contract
  // types both as nullable independently, and a null reaching String() renders
  // the literal text "null".
  const selectedAssignedRows = selectedRows.filter((r) => r.tankVolumeL != null && r.tanksPlanned != null);
  const selectedTanksPlanned = selectedAssignedRows.reduce((sum, r) => sum + (r.tanksPlanned as number), 0);

  const onAssign = async () => {
    if (!programmeId || !roundId || !operatorId || !machineId) return;
    const sessionIds = selectedRows.map((r) => r.sessionId);
    noticeRef.current = false;
    setBulkNotice(null);
    setAssigning(true);
    try {
      // Per-item failures resolve at HTTP 200 as { results, okCount, failCount }
      // — failCount > 0 is a WARNING notice, never a thrown error.
      const out = await nutritionService.assignBulk(programmeId, roundId, sessionIds, operatorId, machineId);
      if (out.failCount > 0) {
        noticeRef.current = true;
        setBulkNotice({
          tone: 'warn',
          text: t('nutrition.foliar.round.bulk.partial', { ok: out.okCount, fail: out.failCount }),
        });
      } else {
        noticeRef.current = true;
        setBulkNotice({ tone: 'success', text: t('nutrition.foliar.round.bulk.assigned', { count: out.okCount }) });
      }
      setSelected([]);
      setOperatorId('');
      setMachineId('');
    } catch {
      noticeRef.current = true;
      setBulkNotice({ tone: 'danger', text: t('nutrition.foliar.round.bulk.failed') });
    } finally {
      setAssigning(false);
      // The roster's combos changed — refresh it. `load()` consults
      // noticeRef itself, so a warn/danger notice set above survives this.
      await load();
    }
  };

  // Distinct from `tableTotals` above: this reads ALL rows (the round's own
  // overall meta line), not the currently filtered/sorted set shown in the
  // roster table.
  const headerTotals = rosterTotals(rows);
  const boardRound: FoliarBoardRound = {
    roundId: round.roundId,
    name: round.name,
    month: round.month,
    plannedStart: round.plannedStart,
    waterRatePerHa: round.waterRatePerHa,
    orchardCount: rows.length,
    productNames: round.productNames,
    sessions: sessionsFromRows(rows),
  };
  const state = roundState(boardRound);

  const blockers: string[] = [];
  if (summary.unassigned > 0) {
    blockers.push(t('nutrition.foliar.round.blocker.unassigned', { count: summary.unassigned }));
  }
  if (summary.awaitingSignoff > 0) {
    blockers.push(t('nutrition.foliar.round.blocker.awaiting', { count: summary.awaitingSignoff }));
  }

  const unassignedCrewLabel = t('nutrition.foliar.round.unassignedCrew');
  const cancelledCrewLabel = t('nutrition.foliar.round.crew.cancelled');

  const chips: FilterBarChip[] = [
    { id: 'all', label: t('nutrition.foliar.round.chips.all'), active: chip === 'all' },
    { id: 'unassigned', label: t('nutrition.foliar.round.chips.unassigned'), active: chip === 'unassigned' },
    { id: 'awaiting', label: t('nutrition.foliar.round.chips.awaiting'), active: chip === 'awaiting' },
    ...summary.cultivars.map((name) => ({
      id: `cultivar:${name}`,
      label: name,
      active: chip === `cultivar:${name}`,
    })),
  ];

  /*
   * Tanks planned: the rounded-up count with the EXACT fraction beside it —
   * `1 (0.55)` — so a part tank is visible rather than implied. 1650 L on a
   * 3000 L machine is one tank a little over half full; a bare "1" hides that.
   *
   * Same rule as the mix preview card: the exact value appears only when it
   * differs from the ceiling, because a whole number in parentheses is noise.
   *
   * ONE helper for the table column and the mobile card. Those two have now
   * drifted apart three times in this feature, always by one of them being
   * edited alone — so they share a function rather than a copied expression.
   *
   * The unassigned guard is on `tankVolumeL`, never on the count: a genuine 0
   * is possible from assignCombo, so `?? '—'` on the count would erase a real
   * answer — while an unassigned row has no count at all, because the figure
   * is written per machine by assignCombo and no machine is assigned yet.
   *
   * #510.4 (D2): `tankVolumeL` and `tanksPlanned` are now governed by ONE
   * agreement predicate in foliarRosterService — one machine AND one tank
   * count — so they are null together. The paired guard below is therefore
   * belt-and-braces rather than load-bearing, and it stays: the wire contract
   * types both as nullable independently, and a null reaching String() renders
   * the literal text "null".
   */
  const tanksPlannedLabel = (r: FoliarRosterRow): string => {
    if (r.tankVolumeL == null || r.tanksPlanned == null) return '—';
    const { waterL, tankVolumeL, tanksPlanned } = r;
    if (!(typeof waterL === 'number' && waterL > 0) || !(tankVolumeL > 0)) return String(tanksPlanned);
    const exact = Math.round(((waterL / tankVolumeL) + Number.EPSILON) * 100) / 100;
    if (exact === tanksPlanned) return String(tanksPlanned);
    return t('nutrition.foliar.round.tanksPlannedExact', { count: tanksPlanned, exact });
  };

  const columns: DataTableColumn<FoliarRosterRow>[] = [
    {
      key: 'select',
      label: (
        <input
          type="checkbox"
          data-testid="round-check-all"
          checked={allChecked}
          onChange={() => setSelected(allChecked ? [] : orderedNames.slice())}
        />
      ),
      render: (r) => (
        <>
          {/* Hidden per-row findability summary — `round-row-<id>` has to
              carry facts drawn from SEVERAL columns (cultivar, crew), and
              `rowProps` cannot accept `data-testid` (see file/task notes),
              so this lives inside a column's `render` instead. `.visually-hidden`
              only hides it VISUALLY — it stays in the accessibility tree — so
              `aria-hidden="true"` is required too, or a screen reader announces
              this test-only scaffolding as loose unlabelled text ahead of the
              row's real (and properly labelled) cultivar/crew cells. */}
          <span className="visually-hidden" aria-hidden="true" data-testid={`round-row-${r.orchardNo}`}>
            {r.orchardNo} {r.cultivar} {crewSummaryFor(r, unassignedCrewLabel)}
          </span>
          <input
            type="checkbox"
            data-testid={`round-check-${r.orchardNo}`}
            checked={selected.includes(r.orchardNo)}
            onChange={(e) => onToggle(r.orchardNo, (e.nativeEvent as MouseEvent).shiftKey)}
          />
        </>
      ),
    },
    { key: 'orchardNo', label: t('nutrition.foliar.round.columns.orchard'), sortable: true },
    { key: 'cultivar', label: t('nutrition.foliar.round.columns.cultivar'), sortable: true },
    {
      key: 'areaHa',
      label: t('nutrition.foliar.round.columns.areaHa'),
      sortable: true,
      align: 'right',
      // #460 (Task 7 extension): areaHa is nullable now, and DataTable's
      // defaultRender (no `render` supplied) returns null for anything that
      // isn't a string/number — React renders that as NOTHING, leaving a
      // dangling cell rather than a dash. `== null`, not `??`, so a legacy
      // `undefined` payload also renders the dash, and a genuine 0 survives.
      render: (r) => (
        <span data-testid={`frp-row-${r.orchardNo}-ha`}>{r.areaHa == null ? '—' : r.areaHa}</span>
      ),
    },
    {
      key: 'waterL',
      label: t('nutrition.foliar.round.columns.waterL'),
      sortable: true,
      align: 'right',
      // Same defaultRender gap as areaHa above.
      render: (r) => (
        <span data-testid={`frp-row-${r.orchardNo}-waterL`}>{r.waterL == null ? '—' : r.waterL}</span>
      ),
    },
    {
      key: 'tanksPlanned',
      label: t('nutrition.foliar.round.columns.tanksPlanned'),
      sortable: true,
      align: 'right',
      // Dose-C fix: tanksPlanned is a combo's own plannedTankCount, written by
      // assignCombo from that combo's machine (#461). It is absent whenever
      // assignCombo could not derive one — see planUnavailableReason for the
      // causes; a machine with no tank volume is one of them, not the only
      // one. Same as the per-tank dose (Dose-B2), one column over. `?? '—'`
      // would be wrong here: a genuine 0 is possible from assignCombo.
      //
      // #460 (Task 7 extension): tanksPlannedLabel guards on BOTH
      // `tankVolumeL == null` (unassigned — no machine, so no count was ever
      // written) AND `tanksPlanned == null` (a
      // resolvable machine can still leave tanksPlanned itself unknowable,
      // D5's 2+-distinct-machines case — see the helper's own comment
      // above). Both use `== null`, never `??`, so a genuine 0 from
      // assignCombo still renders as 0.
      render: (r) => (
        <span data-testid={`round-tanks-${r.orchardNo}`}>{tanksPlannedLabel(r)}</span>
      ),
    },
    {
      key: 'tanksApplied',
      label: t('nutrition.foliar.round.columns.tanksApplied'),
      sortable: true,
      align: 'right',
      // `?? '—'`, never `|| '—'` — a genuine captured 0 must render as 0, not
      // a dash. Only `null` (nothing captured) renders as the dash.
      render: (r) => (
        <span data-testid={`round-applied-${r.orchardNo}`}>{r.tanksApplied ?? '—'}</span>
      ),
    },
    {
      key: 'crew',
      label: t('nutrition.foliar.round.columns.crew'),
      render: (r) => (
        <span className="ntr-rst-crew">
          {renderCrew(r, unassignedCrewLabel, cancelledCrewLabel, `round-crew-${r.orchardNo}`)}
        </span>
      ),
    },
    {
      key: 'status',
      label: t('nutrition.foliar.round.columns.status'),
      sortable: true,
      render: (r) => (
        <div data-testid={`round-status-${r.orchardNo}`}>
          <StatusPill variant={ROW_STATUS_VARIANT[r.status] ?? 'queued'}>
            {t(`nutrition.sessions.status.${r.status}`)}
          </StatusPill>
        </div>
      ),
    },
    {
      key: 'expand',
      label: '',
      render: (r) => (
        <button
          type="button"
          className="pat-btn pat-btn--subtle ntr-rst-expand-toggle"
          data-testid={`round-expand-${r.orchardNo}`}
          aria-expanded={expanded.has(r.orchardNo)}
          aria-label={t('nutrition.foliar.round.expand.toggle', { name: r.orchardNo })}
          onClick={(e) => {
            e.stopPropagation();
            toggleExpanded(r.orchardNo);
          }}
        >
          {expanded.has(r.orchardNo) ? '▾' : '▸'}
        </button>
      ),
    },
  ];

  // Shared by the desktop `expandedRowRender` AND the mobile `mobileCardRow`
  // below — one source of truth for the detail content so the two views
  // cannot drift. Review finding (Task 10, Critical): DataTable's expansion
  // <tr> lives INSIDE the <table>, and Patterns.css:700-703 sets
  // `display:none` on that whole table below 768px whenever `mobileCardRow`
  // is supplied. Relying on expandedRowRender alone therefore makes the
  // card's expand toggle inert on a real phone — the chevron flips but no
  // detail ever appears, because the only copy of the detail is inside the
  // now-hidden table. The card must render its OWN copy, so `testId` is a
  // parameter: the desktop and card copies exist in the DOM simultaneously
  // in jsdom (no CSS there), so they need distinct ids or `getByTestId`
  // throws on multiple matches.
  // Dose-B2 fix: dose per 100 L is machine-independent (totalQty /
  // (totalWaterL / 100)) and is shown for EVERY row — it is how the
  // programme actually states the dose, and it is correct whether or not a
  // machine is assigned. Per-tank is shown ADDITIONALLY, and only once
  // `tankVolumeL` is non-null (a real machine assigned): `perTankMix` comes
  // off the row's reference combo and is `[]` on an unassigned row (#461), so
  // the heading — which names the tank size — would otherwise announce a mix
  // with no lines under it. The two are read from the SAME combo so the size
  // in the heading always belongs to the doses beneath it.
  const renderRowDetail = (r: FoliarRosterRow, testId: string) => (
    <div className="ntr-rst-detail" data-testid={testId}>
      <div className="ntr-rst-detail__section">
        <span className="ntr-rst-detail__heading">{t('nutrition.foliar.round.expand.mixPer100L')}</span>
        {r.mixPer100L.length === 0 ? (
          <p className="ntr-rst-detail__empty">{t('nutrition.foliar.round.expand.noMix')}</p>
        ) : (
          <ul className="ntr-rst-detail__mix" data-testid={`${testId}-per100`}>
            {r.mixPer100L.map((line) => (
              <li key={line.productId}>
                {line.productName} · {line.dosePer100L ?? '—'} {line.doseUnit} / 100 L
              </li>
            ))}
          </ul>
        )}
      </div>
      {r.tankVolumeL != null ? (
        <div className="ntr-rst-detail__section">
          <span className="ntr-rst-detail__heading">
            {t('nutrition.foliar.round.expand.mixPerTank', { volume: r.tankVolumeL })}
          </span>
          <ul className="ntr-rst-detail__mix" data-testid={`${testId}-pertank`}>
            {r.perTankMix.map((line) => (
              <li key={line.productId}>{line.productName} · {line.dosePerTank} {line.doseUnit}</li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="ntr-rst-detail__empty" data-testid={`${testId}-no-machine`}>
          {t('nutrition.foliar.round.expand.noMachine')}
        </p>
      )}
      <div className="ntr-rst-detail__section">
        <span className="ntr-rst-detail__heading">{t('nutrition.foliar.round.expand.crew')}</span>
        <p>{renderCrew(r, unassignedCrewLabel, cancelledCrewLabel, `${testId}-crew`)}</p>
      </div>
      <Link className="ntr-rst-detail__link" to={`/nutrition/foliar/sessions/${r.sessionId}`}>
        {t('nutrition.foliar.round.expand.openSession')}
      </Link>
    </div>
  );

  return (
    <div className="pat-page" data-testid="foliar-round-page">
      <PageHeader
        title={`${round.name} · ${formatMonthName(round.month, i18n.language)}`}
        // Parent spec §4.2 lists three header actions: Edit round, Print
        // instructions, Create missing sessions. Only Edit round is wired
        // here:
        //   - Print instructions IS backed: GET /sessions/:id/instruction/pdf
        //     (nutrition.js, streamInstruction) with a client wrapper at
        //     nutritionSessionService.getInstructionPdf. An earlier revision of
        //     this comment claimed no foliar endpoint existed — that was wrong,
        //     from checking only the /soil-application-sessions/* routes.
        //     It stays unwired here because the sheet is blocked with a 409
        //     when there is NO PRINTABLE COMBO — see resolveInstructionCombos
        //     and classifyInstructionCombos. That covers an all-completed or
        //     all-stood-down session too, with every machine still assigned,
        //     so a header button would need the per-row state to know when it
        //     is even usable. That conclusion is MORE true than when this
        //     comment was written, not less; only its stated reason was wrong.
        //   - Create missing sessions already exists as per-round conversion
        //     on the rounds board (F1, FoliarBoardPage's onCreate). Adding a
        //     second entry point to the same write here would duplicate it.
        // Both are deliberately deferred — do not re-add them from the spec
        // alone without first closing these two gaps.
        actions={(
          <button
            type="button"
            className="pat-btn"
            data-testid="round-edit"
            onClick={() => navigate(`/nutrition/foliar/programmes/${programmeId}/rounds/${roundId}/edit`)}
          >
            {t('nutrition.foliar.round.editRound')}
          </button>
        )}
        meta={(
          <>
            <div data-testid="round-status">
              <StatusPill variant={ROUND_STATE_VARIANT[state]}>
                {t(`nutrition.foliar.board.status.${state}`)}
              </StatusPill>
            </div>
            <div data-testid="round-meta">
              {t('nutrition.foliar.round.meta', {
                orchards: headerTotals.orchards,
                products: round.productNames.join(', '),
                ratePerHa: round.waterRatePerHa ?? '—',
                totalL: headerTotals.waterL,
              })}
            </div>
          </>
        )}
      />

      {round.unitConflicts.length > 0 && (
        <Notice tone="danger" testId="round-unit-conflict">
          {t('nutrition.foliar.round.unitConflict', {
            products: round.unitConflicts.map((c) => c.productName).join(', '),
          })}
        </Notice>
      )}

      {blockers.length > 0 && (
        <Notice tone="warn" testId="round-blocker">{blockers.join(' · ')}</Notice>
      )}

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('nutrition.foliar.round.searchPlaceholder')}
        chips={chips}
        onChipClick={setChip}
      />

      <DataTable<FoliarRosterRow>
        columns={columns}
        footerLabel={t('nutrition.foliar.round.totals')}
        /*
         * Index-aligned to `columns`, but built BY KEY so reordering a column
         * moves its total with it. A positional literal would silently put the
         * water total under "area" the first time someone swaps two columns.
         *
         * This replaces a flex <div> of bare numbers rendered after the table,
         * which had no relationship to the columns above it.
         */
        footerRow={columns.map((c) => {
          switch (c.key) {
            case 'orchardNo': return tableTotals.orchards;
            // #460 (Task 7 fix): a roster where every SHOWN row's area/water
            // is unknown must not render a bare 0 here — that is "a claim,
            // not an absence", the same #460 defect the roster cells
            // (:483-495) and the tanksPlanned total two cases below already
            // guard against. `areaHaKnownRows`/`waterLKnownRows` come from
            // `rosterTotals` itself, so the dash and the sum can never
            // disagree about which rows contributed.
            case 'areaHa':    return tableTotals.areaHaKnownRows > 0 ? tableTotals.areaHa : '—';
            case 'waterL':    return tableTotals.waterLKnownRows > 0 ? tableTotals.waterL : '—';
            // rosterTotals already excludes unassigned rows, which carry no
            // planned tank count at all (see its own comment). Said explicitly
            // in the label because every other figure totals EVERY row, so a
            // bare number here would misread as covering the whole round.
            // No testid here: footerRow is rendered by BOTH the <tfoot> and the
            // card footer, so an id inside a cell would exist twice in jsdom.
            // Tests scope by container and `data-col` instead.
            case 'tanksPlanned': return tableTotals.tanksPlannedAssignedRows > 0
              ? t('nutrition.foliar.round.totalsTanksAssigned', { count: tableTotals.tanksPlanned })
              : '—';
            case 'tanksApplied': return tableTotals.tanksApplied;
            default: return null;
          }
        })}
        rows={sortedShown}
        rowKey={(r) => r.orchardNo}
        rowProps={(r) => (
          { 'data-rowkey': r.orchardNo } as React.HTMLAttributes<HTMLTableRowElement> & { draggable?: boolean }
        )}
        // Both sort AND onSortChange — DataTable's sort glyph is CONTROLLED
        // and renders inert without both wired (see task notes).
        sort={sort}
        onSortChange={(s) => setSort({ key: s.key as RosterSortKey, direction: s.direction })}
        emptyState={t('nutrition.foliar.round.empty')}
        // Task 10: expand-to-manage. `expandedRowKey` is passed the Set
        // itself (not a bare string) — DataTable.tsx:38-44 documents that a
        // string collapses the previously expanded row, which would fail
        // the "expands rows independently" test.
        expandedRowRender={(r) => renderRowDetail(r, `round-detail-${r.orchardNo}`)}
        expandedRowKey={expanded}
        // Task 10 addition: nine columns, no card layout below 768px, per
        // design-system/page-patterns.md:179 ("Tables with ≥ 3 columns
        // convert to card-row layout"). The card carries the selection
        // checkbox (bulk assign is the whole point of this screen), the
        // orchard identity and its key facts, the crew, and its own expand
        // toggle.
        //
        // Review finding (Critical): the toggle must render the detail
        // INSIDE the card, not rely on expandedRowRender's <tr>.
        // Patterns.css:700-703 sets `display:none` on the entire <table>
        // (expandedRowRender's <tr> included) below 768px whenever
        // mobileCardRow is supplied — jsdom applies no CSS, so both copies
        // exist in the test DOM regardless, but on a real phone the table
        // copy is unreachable. The card must own its own detail render.
        mobileCardRow={(r) => (
          <div className="ntr-rst-card">
            <div className="ntr-rst-card__head">
              <input
                type="checkbox"
                data-testid={`round-check-card-${r.orchardNo}`}
                checked={selected.includes(r.orchardNo)}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => onToggle(r.orchardNo, (e.nativeEvent as MouseEvent).shiftKey)}
              />
              <span className="ntr-rst-card__title">{r.orchardNo} · {r.cultivar}</span>
              <StatusPill variant={ROW_STATUS_VARIANT[r.status] ?? 'queued'}>
                {t(`nutrition.sessions.status.${r.status}`)}
              </StatusPill>
            </div>
            <div className="ntr-rst-card__facts">
              {/* #460 (Task 7 extension): areaHa/waterL are nullable now — a
                  bare `{r.areaHa}` renders NOTHING for null (React drops
                  null/undefined children silently), leaving a dangling
                  label with no value beside it rather than an em dash. */}
              {t('nutrition.foliar.round.columns.areaHa')}:
              {' '}
              <span data-testid={`round-area-card-${r.orchardNo}`}>{r.areaHa == null ? '—' : r.areaHa}</span>
              {' · '}
              {t('nutrition.foliar.round.columns.waterL')}:
              {' '}
              <span data-testid={`round-water-card-${r.orchardNo}`}>{r.waterL == null ? '—' : r.waterL}</span>
              {' · '}
              {t('nutrition.foliar.round.columns.tanksPlanned')}:
              {' '}
              {/* Dose-C fix: same guard as the desktop table's `tanksPlanned`
                  column — the card and table must agree (see that column's
                  own comment). A distinct testid because the table's own
                  copy (`round-tanks-<no>`) exists simultaneously in jsdom. */}
              <span data-testid={`round-tanks-card-${r.orchardNo}`}>{tanksPlannedLabel(r)}</span>
            </div>
            <div className="ntr-rst-card__crew">
              {renderCrew(r, unassignedCrewLabel, cancelledCrewLabel, `round-crew-card-${r.orchardNo}`)}
            </div>
            <button
              type="button"
              className="pat-btn pat-btn--subtle ntr-rst-expand-toggle"
              data-testid={`round-expand-card-${r.orchardNo}`}
              aria-expanded={expanded.has(r.orchardNo)}
              aria-label={t('nutrition.foliar.round.expand.toggle', { name: r.orchardNo })}
              onClick={(e) => {
                e.stopPropagation();
                toggleExpanded(r.orchardNo);
              }}
            >
              {expanded.has(r.orchardNo) ? '▾' : '▸'}
            </button>
            {expanded.has(r.orchardNo) && renderRowDetail(r, `round-detail-card-${r.orchardNo}`)}
          </div>
        )}
      />


      {/* Rendered independently of `selected.length` — a bulk-assign result
          notice must survive `onAssign` clearing `selected` on success, and
          must stay visible after the refresh on a partial failure. */}
      {bulkNotice && (
        <Notice tone={bulkNotice.tone} testId="round-bulk-notice">{bulkNotice.text}</Notice>
      )}

      {selected.length > 0 && (
        <div className="ntr-rst-bulkbar" data-testid="round-bulk-bar">
          <div className="ntr-rst-bulkbar__row">
            <span className="ntr-rst-bulkbar__count">
              {t('nutrition.foliar.round.bulk.selected', { count: selected.length })}
              {/* Dose-C fix: omit the tanks figure entirely rather than show
                  a sum of zero across unassigned rows, which would read as
                  "0 tanks needed" — a claim, not an absence of data. When
                  shown, the label says "assigned only" (see rosterTotals'
                  own comment on the same guard for the totals row). */}
              {selectedAssignedRows.length > 0 && (
                <>
                  {' · '}
                  <span data-testid="round-bulk-tanks">
                    {t('nutrition.foliar.round.totalsTanksAssigned', { count: selectedTanksPlanned })}
                  </span>
                </>
              )}
            </span>
            <label className="ntr-rst-bulkbar__label" htmlFor="round-bulk-operator">
              {t('nutrition.foliar.round.bulk.operator')}
            </label>
            <select
              id="round-bulk-operator"
              data-testid="round-bulk-operator"
              className="pat-input"
              value={operatorId}
              onChange={(e) => setOperatorId(e.target.value)}
            >
              <option value="">{t('nutrition.foliar.round.bulk.operator')}</option>
              {operators.map((o) => (
                <option key={o._id} value={o._id}>{o.name}</option>
              ))}
            </select>
            <label className="ntr-rst-bulkbar__label" htmlFor="round-bulk-machine">
              {t('nutrition.foliar.round.bulk.machine')}
            </label>
            <select
              id="round-bulk-machine"
              data-testid="round-bulk-machine"
              className="pat-input"
              value={machineId}
              onChange={(e) => setMachineId(e.target.value)}
            >
              <option value="">{t('nutrition.foliar.round.bulk.machine')}</option>
              {machines.map((m) => (
                <option key={m._id} value={m._id}>{m.name}</option>
              ))}
            </select>
            <button
              type="button"
              className="pat-btn pat-btn--primary"
              data-testid="round-bulk-assign"
              disabled={!operatorId || !machineId || assigning}
              onClick={onAssign}
            >
              {t('nutrition.foliar.round.bulk.assign')}
            </button>
            <button
              type="button"
              className="pat-btn"
              data-testid="round-bulk-clear"
              onClick={() => {
                setSelected([]);
                setOperatorId('');
                setMachineId('');
              }}
            >
              {t('nutrition.foliar.round.bulk.clear')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FoliarRoundPage;
